/**
 * useStaffInRegion — active staff for the crew picker modal (BON-200). Backed
 * by `GET /api/v1/staff`, which the backend intersects with the caller's own
 * scope. Only ACTIVE staff are assignable to a crew slot (the PUT /:id/staff
 * write rejects inactive members), so the list is filtered to `isActive` here.
 *
 * `scope` (BON-390): `'region'` (default) filters to the given region;
 * `'all'` drops the region filter so out-of-region staff can be assigned.
 * Admins and regional directors get the whole roster, including staff with no
 * region; anyone limited to their own regions gets only staff in those.
 */
import { useQuery } from '@tanstack/react-query';

import { fetchStaff, type StaffMember } from '../../../../api/staff';

const STALE_60S = 60_000;

export function useStaffInRegion(regionId: string | null, scope: 'region' | 'all' = 'region') {
  const all = scope === 'all';
  return useQuery<StaffMember[]>({
    queryKey: ['staff', 'in-region', all ? 'all' : regionId],
    queryFn: async () =>
      (await fetchStaff(all ? {} : { regionId: regionId ?? undefined })).filter((s) => s.isActive),
    enabled: all || !!regionId,
    staleTime: STALE_60S,
  });
}
